/**
 * Artifact export - copy, and download as .docx.
 *
 * No new dependency and no backend endpoint: `utils/docxConverter` already
 * exports `htmlToDocxBlob`, built on the `docx` package the app ships. What
 * was missing is a way to turn a TYPED research payload into the HTML that
 * converter expects, which is what this file adds.
 *
 * Generation walks the typed payload rather than the rendered DOM: the export
 * then carries the document's real structure (clauses, dated events, memo
 * sections) instead of whatever the screen happened to show.
 */

import { htmlToDocxBlob } from '../../utils/docxConverter';
import { DocBlock, ResearchDocument, buildDocument } from './documentModel';
import { ResearchResponse } from './types';

/* ------------------------------------------------------------------ */
/*  Markdown → HTML (only what a legal document actually uses)         */
/* ------------------------------------------------------------------ */

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/** Inline emphasis, after escaping - bold and italic only. */
function inlineMarkdown(text: string): string {
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>');
}

/**
 * A deliberately small markdown subset: headings, list items, paragraphs.
 *
 * A full parser would be a new dependency for output that is generated by our
 * own prompt and only ever uses these constructs.
 */
export function markdownToHtml(markdown: string): string {
  if (!markdown) return '';
  const out: string[] = [];
  let inList = false;

  const closeList = () => {
    if (inList) {
      out.push('</ul>');
      inList = false;
    }
  };

  markdown.split('\n').forEach((raw) => {
    const line = raw.trim();

    if (!line) {
      closeList();
      return;
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      closeList();
      const level = Math.min(heading[1].length, 6);
      out.push(`<h${level}>${inlineMarkdown(heading[2])}</h${level}>`);
      return;
    }

    const bullet = line.match(/^[-*]\s+(.*)$/);
    if (bullet) {
      if (!inList) {
        out.push('<ul>');
        inList = true;
      }
      out.push(`<li>${inlineMarkdown(bullet[1])}</li>`);
      return;
    }

    closeList();
    out.push(`<p>${inlineMarkdown(line)}</p>`);
  });

  closeList();
  return out.join('\n');
}

/* ------------------------------------------------------------------ */
/*  Document → HTML / text                                             */
/* ------------------------------------------------------------------ */

/** Footnote markers, rendered as a superscript run after the text. */
function markersHtml(numbers?: number[]): string {
  if (!numbers || numbers.length === 0) return '';
  return numbers.map((n) => `<sup>${n}</sup>`).join('');
}

function blockHtml(block: DocBlock): string {
  switch (block.kind) {
    case 'heading':
      return `<h2>${escapeHtml(block.text)}</h2>`;
    case 'paragraph':
      return `<p>${escapeHtml(block.text)}${markersHtml(block.footnotes)}</p>`;
    case 'list':
      return `<ul>${block.items.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}</ul>`;
    case 'markdown':
      return markdownToHtml(block.text);
    case 'event': {
      const head = [block.date, block.approximate ? '~' : '']
        .filter(Boolean).join(' ');
      const parts = [
        `<p><strong>${escapeHtml(head)}</strong>${head && block.text ? ' - ' : ''}` +
          `${escapeHtml(block.text)}${markersHtml(block.footnotes)}</p>`,
      ];
      if (block.significance) parts.push(`<p>${escapeHtml(block.significance)}</p>`);
      return parts.join('\n');
    }
    default:
      return '';
  }
}

/**
 * The document as standalone HTML - the SAME assembly the pane renders.
 *
 * Blocks are emitted one per line so the docx converter sees discrete
 * paragraphs; running them together is what made sections collide.
 */
export function documentToHtml(doc: ResearchDocument): string {
  const parts: string[] = [];
  if (doc.title) parts.push(`<h1>${escapeHtml(doc.title)}</h1>`);
  doc.blocks.forEach((block) => {
    const html = blockHtml(block);
    if (html) parts.push(html);
  });
  if (doc.footnotes.length) {
    // No heading: a numbered list after a rule needs no label, and any label
    // we invented would be English inside a document that is not.
    parts.push('<hr />');
    parts.push(
      `<ol>${doc.footnotes
        .map((note) => {
          const detail = note.detail ? ` - ${escapeHtml(note.detail)}` : '';
          return `<li>${escapeHtml(note.label)}${detail}</li>`;
        })
        .join('')}</ol>`
    );
  }
  return parts.join('\n');
}

/** The document as plain text, from the same blocks. */
export function documentToText(doc: ResearchDocument): string {
  const lines: string[] = [];
  if (doc.title) lines.push(doc.title, '');
  doc.blocks.forEach((block) => {
    switch (block.kind) {
      case 'heading':
        lines.push(block.text, '');
        break;
      case 'paragraph':
        if (block.text) lines.push(block.text, '');
        break;
      case 'list':
        block.items.forEach((item) => lines.push(`- ${item}`));
        lines.push('');
        break;
      case 'markdown':
        lines.push(block.text, '');
        break;
      case 'event':
        lines.push([block.date, block.text].filter(Boolean).join(' - '));
        if (block.significance) lines.push(block.significance);
        lines.push('');
        break;
      default:
        break;
    }
  });
  doc.footnotes.forEach((note, i) => {
    lines.push(`${i + 1}. ${note.label}${note.detail ? ` - ${note.detail}` : ''}`);
  });
  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * The artifact as standalone HTML.
 *
 * Falls back to the server-rendered markdown whenever the typed payload is
 * unusable - handled inside `buildDocument`, so both paths stay identical.
 */
export function artifactToHtml(response: ResearchResponse, title = ''): string {
  return documentToHtml(buildDocument(response, title));
}

/** Plain text for the clipboard - the same document, serialised as text. */
export function artifactToText(response: ResearchResponse, title = ''): string {
  return documentToText(buildDocument(response, title));
}

/** A filesystem-safe file name, without the extension. */
export function artifactFileName(title: string): string {
  const safe = (title || 'research-document')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 60);
  return safe || 'research-document';
}

/**
 * Build the .docx. Returns the blob so callers own the download, which keeps
 * this testable without touching the DOM.
 */
export async function artifactToDocxBlob(
  response: ResearchResponse,
  title: string
): Promise<Blob> {
  return htmlToDocxBlob(artifactToHtml(response, title), artifactFileName(title));
}

/** Trigger a browser download for an already-built blob. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
